import { memo, useState, useCallback, useEffect, useRef } from "react";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Button, Typography, Box, Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useGlobalState } from "../../contexts/GlobalState";
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { useCreateMaterialContainer, useValidateMatIdForLabelPrinting, useFetchMaterialDetails, useGetSPLotbyId, useGetDrillDownSPLotById } from '../../api/vegApl';
import { useSnackbar } from '../../contexts/SnackbarContext';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CloseIcon from '@mui/icons-material/Close';
import ScanInput from '../ScanInput';
import ScanButton from './ScanIcon';
import PrintSPLotLabelDialog from '../common/PrintSPLotLabelDialog';
import { RegisterToMintDialog } from '../mintRegistration/RegisterToMintDialog';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { normalizeMaterialRowData } from '../../utils/LabelFieldMapper';
import { usePrintLabel } from '../../hooks/usePrintLabel';
import type { LabelTemplate } from '../../api/vegApl/labelTemplate';

const getErrorMessage = (error: any, fallback: string): string => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
        return detail[0]?.msg || fallback;
    }
    if (typeof detail === 'string') {
        return detail;
    }
    return error?.message || fallback;
};

const normalizeIdentifier = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const buildMaterialIdentifiers = (value: any): string[] => {
    if (!value) return [];

    const candidates = [
        value?.material_id,
        value?.matid,
        value?.matId,
        value?.material_alt_id,
        value?.container_alt_id,
        value?.container_id,
        value?.material_container_id,
    ];

    return candidates.map(normalizeIdentifier).filter(Boolean);
};
export type SPLotDialogprops = {
    open: boolean,
    drillDownCurrRow: any,
    setCreateLableModal: (open: boolean) => void,
    onSuccess?: () => void,
    showRegisterToMint?: boolean,
    materialTemplates?: LabelTemplate[]
}

function CreateLabelDialog({ open, drillDownCurrRow, setCreateLableModal, onSuccess, showRegisterToMint = false, materialTemplates = [] }: SPLotDialogprops) {
    const classes = useCommonStyles();
    const { state: globalState, crops, sites } = useGlobalState();
    const [scannedValue, setScannedValue] = useState("");
    const scannedValueRef = useRef("");
    const [triggerScan, setTriggerScan] = useState(false);
    const [registerToMint, setRegisterToMint] = useState(true);
    const manualEdit = true;
    const errorText = ""
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);

    // Print label dialog state (material mode)
    const {
        printLabelOpen, printLabelRow: materialRowData, hasTemplate, foundTemplate,
        openPrintDialog: openMaterialPrintDialog, openPrintDialogWithTemplate, closePrintDialog,
    } = usePrintLabel({ templateType: 'Material', defaultSite: globalState.siteSelected || 'MAAG' });
    const [registerToMintOpen, setRegisterToMintOpen] = useState(false);
    const [isNewContainer, setIsNewContainer] = useState(true);
    const isActionInProgress = isLoading;

    const registerToMintRef = useRef(registerToMint);
    registerToMintRef.current = registerToMint;

    const validationResponseRef = useRef<any>(null);
    const materialDetailsRef = useRef<any>(null);
    const mergedDataForPrintRef = useRef<Record<string, any> | null>(null);
    const materialIsRegisteredRef = useRef(false);
    const materialStatusMapRef = useRef<Record<string, string>>({});
    const scanInputRef = useRef<HTMLInputElement>(null);

    const focusScanInput = useCallback(() => {
        window.setTimeout(() => {
            scanInputRef.current?.focus();
            scanInputRef.current?.select();
        }, 0);
    }, []);

    const handleDialogClose = useCallback((_: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        setCreateLableModal(false);
    }, [setCreateLableModal]);

    const proceedWithCreateMaterial = useCallback((matid: string) => {
        const req: { matid: string; splot_id: string; created_by: string; register_to_mint?: boolean } = {
            splot_id: String(drillDownCurrRow?.splot_id ?? ''),
            matid,
            created_by: String(globalState.user_id_db ?? ''),
        };
        if (showRegisterToMint) {
            req.register_to_mint = registerToMintRef.current;
        }
        createMaterialContainer({ data: req });
    }, [drillDownCurrRow, globalState.user_id_db, showRegisterToMint]);

    /** Shared finalizer: normalize merged data → reset state → open print dialog */
    const finalizeMaterialAndOpenPrint = useCallback((mergedData: Record<string, any>) => {
        const validationData = validationResponseRef.current ?? {};
        const template = validationData?.template ?? null;

        // Flatten splotDetails — if it's an array, take [0] so nested paths like 'splotDetails.trial_id' resolve
        if (Array.isArray(mergedData.splotDetails)) {
            mergedData.splotDetails = mergedData.splotDetails[0] ?? {};
        }

        const normalizedRow = normalizeMaterialRowData(mergedData, crops, sites);
        setIsLoading(false);
        setScannedValue("");
        scannedValueRef.current = "";
        openPrintDialogWithTemplate(normalizedRow, template);

        validationResponseRef.current = null;
        materialDetailsRef.current = null;
    }, [crops, sites, setCreateLableModal, openPrintDialogWithTemplate]);

    const { mutate: getSPLotbyId } = useGetSPLotbyId({
        mutation: {
            onSuccess: (response: any) => {
                const splotData = response?.data ?? {};
                const validationData = validationResponseRef.current ?? {};
                const materialData = materialDetailsRef.current ?? {};
                // Unwrap array — /material_details returns [{trial_bid, ...}]; spread must be on the item, not the array
                const materialItem = Array.isArray(materialData) ? (materialData[0] ?? {}) : materialData;
                const merged = { ...validationData, ...materialItem, splotDetails: splotData };
                mergedDataForPrintRef.current = merged;

                // Fetch drill-down to check material_status before proceeding
                const splotId = Array.isArray(splotData)
                    ? splotData[0]?.splot_id
                    : splotData?.splot_id;
                if (splotId) {
                    getDrillDownMaterialStatus({ splot_id: String(splotId) });
                } else {
                    // No splot_id available — skip status check and proceed
                    materialIsRegisteredRef.current = false;
                    finalizeMaterialAndOpenPrint(merged);
                }
            },
            onError: (error: any) => {
                const errorMessage = getErrorMessage(error, 'Failed to fetch SP Lot details');
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);
            },
        },
    });

    const { mutate: getDrillDownMaterialStatus } = useGetDrillDownSPLotById({
        mutation: {
            onSuccess: (response: any) => {
                const materials: any[] = response?.data?.materials ?? [];

                const validationData = validationResponseRef.current ?? {};
                const details = materialDetailsRef.current ?? {};
                const firstMaterialDetails = Array.isArray(details) ? (details[0] ?? {}) : details;
                const targetIdentifiers = new Set([
                    ...buildMaterialIdentifiers(validationData),
                    ...buildMaterialIdentifiers(firstMaterialDetails),
                    normalizeIdentifier(scannedValueRef.current),
                ].filter(Boolean));

                const matchedMaterial = materials.find((material: any) => {
                    const materialIdentifiers = buildMaterialIdentifiers(material);
                    return materialIdentifiers.some((id) => targetIdentifiers.has(id));
                });

                const matchedStatus = normalizeIdentifier(matchedMaterial?.material_status);
                materialIsRegisteredRef.current = matchedStatus === 'registered';
                materialStatusMapRef.current = {};
                
                if (mergedDataForPrintRef.current) {
                    const mergedWithDrillDown = {
                        ...mergedDataForPrintRef.current,
                        // Consolidated drill-down payload for downstream consumers.
                        drillDownMaterials: materials,
                        drillDownMatchedMaterial: matchedMaterial ?? null,
                        // Keep a normalized status field used by print dialog logic.
                        material_status: matchedMaterial?.material_status ?? mergedDataForPrintRef.current?.material_status,
                        "Current Status": matchedMaterial?.material_status ?? mergedDataForPrintRef.current?.["Current Status"],
                        // Note: Treatments are now handled from /material_details with should_print filtering
                        // No longer override with splot_protocol_steps treatments
                    };
                    finalizeMaterialAndOpenPrint(mergedWithDrillDown);
                    mergedDataForPrintRef.current = null;
                }
            },
            onError: () => {
                // If drill-down fails, fall back to allowing mint registration
                materialStatusMapRef.current = {};
                materialIsRegisteredRef.current = false;
                if (mergedDataForPrintRef.current) {
                    finalizeMaterialAndOpenPrint(mergedDataForPrintRef.current);
                    mergedDataForPrintRef.current = null;
                }
            },
        },
    });

    const { mutate: fetchMaterialDetails } = useFetchMaterialDetails({
        mutation: {
            onSuccess: (data: any) => {
                const details = data?.data ?? {};
                // Store material details for merging after getSPLotbyId
                materialDetailsRef.current = details;

                // Extract splot_id from material details (could be array or object)
                const splotId = Array.isArray(details)
                    ? details[0]?.splot_id
                    : details.splot_id;

                if (splotId) {
                    getSPLotbyId({ splotId: String(splotId) });
                } else {
                    const validationData = validationResponseRef.current ?? {};
                    // Unwrap array before merging so top-level fields like trial_bid are accessible
                    const detailsItem = Array.isArray(details) ? (details[0] ?? {}) : details;
                    finalizeMaterialAndOpenPrint({ ...validationData, ...detailsItem });
                }
            },
            onError: (error: any) => {
                const errorMessage = getErrorMessage(error, 'Failed to fetch material details');
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);
            },
        },
    });

    const { mutate: validateMatId } = useValidateMatIdForLabelPrinting({
        mutation: {
            onSuccess: (data: any) => {
                const responseData = data?.data ?? data ?? {};
                
                // Check if response has a message or error field
                if (responseData?.message || responseData?.error) {
                    const errorMessage = responseData?.message || responseData?.error || 'Validation failed';
                    showSnackbar({ message: errorMessage, severity: 'error' });
                    setIsLoading(false);
                    return;
                }
                
                // Check if responseData is valid (should be an object with container_alt_id)
                if (typeof responseData !== 'object' || responseData === null || !responseData?.container_alt_id) {
                    showSnackbar({ message: 'Invalid response data from validation endpoint', severity: 'error' });
                    setIsLoading(false);
                    return;
                }
                
                const containerAltId = responseData.container_alt_id;
                // Store full validation response for merging after fetchMaterialDetails
                validationResponseRef.current = responseData;
                // Extract isNewContainer value for button label
                setIsNewContainer(responseData?.isNewContainer !== false);
                fetchMaterialDetails({ data: { container_alt_ids: [containerAltId] } });
            },
            onError: (error: any) => {
                const errorMessage = getErrorMessage(error, 'Material ID validation failed');
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);
            },
        },
    });

    const { mutate: createMaterialContainer } = useCreateMaterialContainer({
        mutation: {
            onSuccess: (data: any) => {
                showSnackbar({ message: data?.data?.message, severity: 'success' });
                setIsLoading(false);
                setScannedValue("");
                scannedValueRef.current = "";

                if (showRegisterToMint && registerToMintRef.current) {
                    // Store material data for print dialog
                    const responseData = data?.data ?? {};
                    // Keep scan dialog open for continuous scanning after print flow.
                    openMaterialPrintDialog(
                        responseData,
                        globalState.siteSelected || 'MAAG',
                        responseData.crop_code ?? '',
                    );
                } else {
                    // Keep scan dialog open for continuous label creation.
                }
                onSuccess?.();
            },
            onError: (error: any) => {
                const errorMessage = getErrorMessage(error, 'Error in creating container');
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);               
            },
        },
    })

    const submitMatInfo = useCallback(() => {     
        const currentValue = scannedValueRef.current || scannedValue;
        if (!currentValue?.trim()) {
            showSnackbar({ message: 'Please enter or scan a Material ID', severity: 'warning' });
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        if (showRegisterToMint) {
            // Validate first, then create on success
            validateMatId({ data: { matid: currentValue.trim(), created_by: globalState.user_id_db } });
        } else {
            // Direct create without validation
            proceedWithCreateMaterial(currentValue.trim());
        }
    }, [scannedValue, globalState.user_id_db, showSnackbar, showRegisterToMint, validateMatId, proceedWithCreateMaterial]);

    useEffect(() => {
        if (triggerScan && scannedValue?.trim()) {
            submitMatInfo();
            setTriggerScan(false);
        }
    }, [triggerScan, scannedValue, submitMatInfo]);
   
    useEffect(() => {
        if (!open) {
            setScannedValue("");
            scannedValueRef.current = "";
        }
    }, [open]);

    return (
        <>
            <FullScreenActionLoader open={isActionInProgress} />
            <Dialog
                open={open}
                onClose={handleDialogClose}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "16px",
                        },
                    },
                }}
                sx={(theme) => ({
                    ...MtComStyleObj(theme).dialogBoxStyle
                })}>
                <IconButton
                    aria-label="close"
                    onClick={() => setCreateLableModal(false)}
                    sx={(theme) => ({
                        ...MtComStyleObj(theme).dialogCloseButton,
                        position: 'absolute',
                        top: 25,
                        right: 25,
                        zIndex: 1,
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <Box width="100%" display={"flex"} flexDirection={"column"} alignItems={"center"}>
                        <ScanButton />
                        <Typography component="div" className={classes.labelNoto14500}>
                            {showRegisterToMint ? 'Scan Material ID' : 'Scan Material'}
                        </Typography>
                        <Typography component="div" className={classes.labelNoto16Bold400}>
                            {showRegisterToMint
                                ? 'Enter or Scan Material ID to generate Label'
                                : 'Enter or scan Material ID to generate label'}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ width: "100%", mt: 1 }}>
                            <ScanInput ref={scanInputRef} submitOnScan
                                scanPickupEnabled={!manualEdit} defaultValue={scannedValue}
                                onScan={(val) => {
                                    scannedValueRef.current = val;
                                    setScannedValue(val);
                                }} manualEdit={manualEdit}
                                error={errorText?.length ? true : false}
                                label={'Material Identifier'}
                                errorText={errorText}
                                triggerValidation={triggerScan}
                                placeholder={showRegisterToMint ? "Enter or scan Material ID to generate label" : "Scan or Enter Container ID"}
                                onEnterPress={() => setTriggerScan(true)} />
                            <Button variant="contained" disableRipple className={classes.greenButton} startIcon={<CropFreeIcon />}  onClick={() => setTriggerScan(true)} loading={isLoading} loadingPosition="start">Scan</Button>
                        </Stack>

                    </Box>
                </DialogContent>
            </Dialog>

            {showRegisterToMint && (
                <>
                    <PrintSPLotLabelDialog
                        open={printLabelOpen}
                        onClose={() => {
                            closePrintDialog();
                            focusScanInput();
                            registerToMintRef.current = true; // Reset to default for next scan
                        }}
                        onPrint={() => {
                            closePrintDialog();
                            if (registerToMintRef.current && !materialIsRegisteredRef.current) {
                                setRegisterToMintOpen(true);
                            } else if (registerToMintRef.current && materialIsRegisteredRef.current) {
                                showSnackbar({
                                    message: 'Material already registered cannot proceed with mint registration',
                                    severity: 'error',
                                });
                                onSuccess?.();
                                focusScanInput();
                            } else {
                                // Checkbox unchecked or material already Registered — terminate after print
                                onSuccess?.();
                                focusScanInput();
                            }
                        }}
                        rowData={materialRowData ?? {}}
                        hasTemplate={hasTemplate}
                        foundTemplate={foundTemplate}
                        splotTemplates={materialTemplates}
                        dialogTitle="Print Material Label"
                        primaryIdLabel="Material ID"
                        primaryIdValue={materialRowData?.matId ?? materialRowData?.matid ?? ''}
                        infoFields={[
                            { label: 'SP Lot ID', value: materialRowData?.splotDetails?.splot_id ?? materialRowData?.splot_id ?? '' },
                            { label: 'Container ID', value: materialRowData?.container_alt_id ?? '' },
                        ]}
                        isNewContainer={isNewContainer}
                        showRegisterToMintToggle
                        registerToMint={registerToMint}
                        onRegisterToMintChange={setRegisterToMint}
                    />
                    <RegisterToMintDialog
                        open={registerToMintOpen}
                        onClose={() => {
                            setRegisterToMintOpen(false);
                            focusScanInput();
                        }}
                        materialDetails={materialRowData}
                        onSuccess={() => {
                            setRegisterToMintOpen(false);
                            onSuccess?.();
                            focusScanInput();
                        }}
                    />
                </>
            )}
        </>
    );
}
export default memo(CreateLabelDialog);